import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import type { ComponentType } from "react";

// Retry a failed page load once, then force a fresh reload (stale copy after updates).
function lazyRetry<T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      try {
        await new Promise((r) => setTimeout(r, 500));
        return await factory();
      } catch {
        const key = "chunk-reload-at";
        const last = Number(sessionStorage.getItem(key) || 0);
        if (Date.now() - last > 10000) {
          sessionStorage.setItem(key, String(Date.now()));
          window.location.reload();
          return new Promise<{ default: T }>(() => {});
        }
        throw err;
      }
    }
  });
}

const Index = lazyRetry(() => import("./pages/Index"));
const Auth = lazyRetry(() => import("./pages/Auth"));
const Onboarding = lazyRetry(() => import("./pages/Onboarding"));
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const EditProfile = lazyRetry(() => import("./pages/EditProfile"));
const Install = lazyRetry(() => import("./pages/Install"));
const ContactSupport = lazyRetry(() => import("./pages/ContactSupport"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const Terms = lazyRetry(() => import("./pages/Terms"));
const PrivacyRequest = lazyRetry(() => import("./pages/PrivacyRequest"));
const Invite = lazyRetry(() => import("./pages/Invite"));
const Unsubscribe = lazyRetry(() => import("./pages/Unsubscribe"));
const ResetPassword = lazyRetry(() => import("./pages/ResetPassword"));
const AdminSignIns = lazyRetry(() => import("./pages/AdminSignIns"));
const AdminUsers = lazyRetry(() => import("./pages/AdminUsers"));
const AdminOAuthFailures = lazyRetry(() => import("./pages/AdminOAuthFailures"));
const AdminSeedProfiles = lazyRetry(() => import("./pages/AdminSeedProfiles"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/edit-profile" element={<ProtectedRoute><EditProfile /></ProtectedRoute>} />
              <Route path="/install" element={<Install />} />
              <Route path="/contact-support" element={<ProtectedRoute><ContactSupport /></ProtectedRoute>} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy-request" element={<ProtectedRoute><PrivacyRequest /></ProtectedRoute>} />
              <Route path="/invite" element={<ProtectedRoute><Invite /></ProtectedRoute>} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/admin/sign-ins" element={<ProtectedRoute><AdminSignIns /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/oauth-failures" element={<ProtectedRoute><AdminOAuthFailures /></ProtectedRoute>} />
              <Route path="/admin/seed-profiles" element={<ProtectedRoute><AdminSeedProfiles /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
